import React, { createContext, useContext, useMemo } from 'react';
import { AuthenticationProvider, InMemoryWebStorage, OidcSecure } from '@axa-fr/react-oidc-context';
import { UserManagerSettings } from 'oidc-client';
import { AuthConfigurationContext } from './AuthConfigurationContext';
import { useHostPath } from './Hooks/useHostPath';
import { AuthenticateResources } from '../AuthenticateResources';

const initialState = {
  client_id: undefined, // set by loading api request to backend AuthController
  authority: undefined, // set by loading api request to backend AuthController
  redirect_uri: useHostPath('authentication/callback'),
  response_type: 'code',
  post_logout_redirect_uri: useHostPath(),
  silent_redirect_uri: useHostPath('authentication/silent_callback'),
  automaticSilentRenew: true,
  scope: 'openid profile email'
} as UserManagerSettings;

const OidcContext = createContext<UserManagerSettings>({} as UserManagerSettings);

export const OidcProvider = ({ children }: { children: JSX.Element }): JSX.Element => {
  const authConfig = useContext(AuthConfigurationContext);

  const oidcConfiguration = useMemo<UserManagerSettings>(
    () => ({
      ...initialState,
      authority: authConfig.authority,
      client_id: authConfig.clientId,
      scope: 'openid profile email'
    }),
    [authConfig]
  );

  const Message = (message?: string) => {
    return !message ? null : <h1>{message}</h1>;
  };

  const Blank = () => Message();

  return oidcConfiguration.authority ? (
    <OidcContext.Provider value={oidcConfiguration}>
      <AuthenticationProvider
        configuration={oidcConfiguration}
        UserStore={InMemoryWebStorage}
        notAuthenticated={() => Message('Not authenticated')}
        authenticating={Blank}
        callbackComponentOverride={Blank}
        notAuthorized={Blank}
        sessionLostComponent={Blank}>
        <AuthenticateResources />
        <OidcSecure>{children}</OidcSecure>
      </AuthenticationProvider>
    </OidcContext.Provider>
  ) : (
    <>{children}</>
  );
};
